import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';

// ============================================================================
// AllergenPage — Korean food-safety (알레르기 / 원산지 / 영양) management
// 19 official allergens per 식약처 고시.
// ============================================================================

const KOREAN_ALLERGENS = [
  '알류', '우유', '메밀', '땅콩', '대두', '밀', '고등어', '게', '새우',
  '돼지고기', '복숭아', '토마토', '아황산류', '호두', '닭고기', '쇠고기',
  '오징어', '조개류', '잣',
];

const formatWon = (n) => `₩${Number(n || 0).toLocaleString('ko-KR')}`;

let toastCounter = 0;

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconX = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconInfo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const AllergenPage = () => {
  const { user } = useAuth();
  const storeId = user?.storeId || user?.store_id;

  const [menus, setMenus] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingMenu, setEditingMenu] = useState(null);
  const [editForm, setEditForm] = useState({ allergens: [], origin: '', nutrition: {} });
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((msg, type = 'info') => {
    const id = ++toastCounter;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3200);
  }, []);

  const loadMenus = useCallback(async () => {
    if (!storeId) { setIsLoading(false); return; }
    setIsLoading(true);
    try {
      const res = await api.get(`/stores/${storeId}/menus/meta`);
      const list = res.data?.menus || [];
      setMenus(list.map((m) => ({
        ...m,
        allergens: Array.isArray(m.allergens) ? m.allergens : [],
        nutrition: m.nutrition || {},
      })));
    } catch (err) {
      // Fallback to basic menu list
      try {
        const fb = await api.get(`/stores/${storeId}/menus`);
        const list = fb.data?.menus || [];
        setMenus(list.map((m) => ({
          ...m,
          allergens: (() => { try { return JSON.parse(m.allergens || '[]'); } catch { return []; } })(),
          nutrition: (() => { try { return JSON.parse(m.nutrition || '{}'); } catch { return {}; } })(),
        })));
      } catch {
        addToast('메뉴를 불러오지 못했습니다', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }, [storeId, addToast]);

  useEffect(() => { loadMenus(); }, [loadMenus]);

  const openEdit = (menu) => {
    setEditingMenu(menu);
    setEditForm({
      allergens: [...(menu.allergens || [])],
      origin: menu.origin || '',
      nutrition: { ...(menu.nutrition || {}) },
    });
  };

  const closeEdit = () => {
    setEditingMenu(null);
    setEditForm({ allergens: [], origin: '', nutrition: {} });
  };

  const toggleAllergen = (a) => {
    setEditForm((prev) => {
      const has = prev.allergens.includes(a);
      return {
        ...prev,
        allergens: has ? prev.allergens.filter((x) => x !== a) : [...prev.allergens, a],
      };
    });
  };

  const updateNutrition = (key, value) => {
    setEditForm((prev) => ({ ...prev, nutrition: { ...prev.nutrition, [key]: value } }));
  };

  const save = async () => {
    if (!editingMenu) return;
    setIsSaving(true);
    try {
      const id = editingMenu.id || editingMenu._id;
      const cleanNutrition = Object.fromEntries(
        Object.entries(editForm.nutrition).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      );
      const payload = {
        allergens: editForm.allergens,
        origin: editForm.origin.trim() || null,
        nutrition: Object.keys(cleanNutrition).length ? cleanNutrition : null,
      };
      await api.put(`/stores/${storeId}/menus/${id}/meta`, payload);
      setMenus((prev) => prev.map((m) =>
        (m.id || m._id) === id ? { ...m, ...payload, nutrition: payload.nutrition || {} } : m
      ));
      addToast('저장되었습니다', 'success');
      closeEdit();
    } catch (err) {
      addToast(err.response?.data?.error || '저장에 실패했습니다', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const filtered = menus.filter((m) =>
    !search.trim() || (m.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: menus.length,
    withAllergens: menus.filter((m) => (m.allergens || []).length > 0).length,
    withOrigin: menus.filter((m) => m.origin && m.origin.trim()).length,
  };

  return (
    <div className="page-container">
      {/* Toast container */}
      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.type}`}>
            <span className="toast-icon">
              {t.type === 'success' ? <IconCheck /> : t.type === 'error' ? <IconX /> : <IconInfo />}
            </span>
            <div className="toast-content"><div className="toast-message">{t.msg}</div></div>
          </div>
        ))}
      </div>

      <div className="page-header">
        <div>
          <h1 className="page-title">알레르기 · 원산지 관리</h1>
          <p className="page-subtitle">식품위생법 고시 19대 알레르기 유발 식품 및 원산지를 관리합니다</p>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-icon purple">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"/>
            </svg>
          </div>
          <div className="stat-info">
            <div className="stat-label">전체 메뉴</div>
            <div className="stat-value">{stats.total}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div className="stat-info">
            <div className="stat-label">알레르기 표기</div>
            <div className="stat-value">{stats.withAllergens} / {stats.total}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
          </div>
          <div className="stat-info">
            <div className="stat-label">원산지 표기</div>
            <div className="stat-value">{stats.withOrigin} / {stats.total}</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <input
          className="form-input"
          placeholder="메뉴명 검색..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Menu list */}
      {isLoading ? (
        <div className="card"><div className="card-body">불러오는 중...</div></div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <h3>메뉴가 없습니다</h3>
            <p>먼저 메뉴를 등록해주세요.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {filtered.map((m) => {
              const allergens = m.allergens || [];
              return (
                <div
                  key={m.id || m._id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 18px',
                    borderBottom: '1px solid var(--border)',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 4 }}>
                      {m.name}
                      <span style={{ marginLeft: 8, color: 'var(--text-tertiary)', fontWeight: 500, fontSize: '0.8rem' }}>
                        {formatWon(m.price)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                      {allergens.length === 0 ? (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                          알레르기 정보 없음
                        </span>
                      ) : (
                        allergens.map((a) => (
                          <span
                            key={a}
                            style={{
                              fontSize: '0.72rem',
                              padding: '3px 9px',
                              borderRadius: 999,
                              background: 'var(--danger-light)',
                              color: 'var(--danger)',
                              fontWeight: 600,
                            }}
                          >
                            {a}
                          </span>
                        ))
                      )}
                    </div>
                    {m.origin && (
                      <div style={{ marginTop: 6, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                        원산지: <strong>{m.origin}</strong>
                      </div>
                    )}
                  </div>
                  <button className="btn btn-outline btn-sm" onClick={() => openEdit(m)}>
                    편집
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editingMenu && (
        <>
          <div className="modal-overlay" onClick={closeEdit} />
          <div className="modal modal-lg" role="dialog" aria-label="알레르기 편집">
            <div className="modal-header">
              <div className="modal-title">{editingMenu.name} · 알레르기/원산지</div>
              <button className="modal-close" onClick={closeEdit} aria-label="닫기">×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">19대 알레르기 유발 식품 (다중 선택)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {KOREAN_ALLERGENS.map((a) => {
                    const active = editForm.allergens.includes(a);
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => toggleAllergen(a)}
                        style={{
                          padding: '8px 14px',
                          borderRadius: 999,
                          border: active ? '1px solid var(--danger)' : '1px solid var(--border)',
                          background: active ? 'var(--danger-light)' : 'var(--surface)',
                          color: active ? 'var(--danger)' : 'var(--text-secondary)',
                          fontWeight: 600,
                          fontSize: '0.82rem',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {a}
                      </button>
                    );
                  })}
                </div>
                <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                  선택됨: {editForm.allergens.length}개
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">원산지 표시</label>
                <input
                  className="form-input"
                  placeholder="예) 쇠고기: 국내산 한우 / 돼지고기: 국내산"
                  value={editForm.origin}
                  onChange={(e) => setEditForm({ ...editForm, origin: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">영양 정보 (선택)</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                  {[
                    { key: 'calories', label: '칼로리 (kcal)' },
                    { key: 'protein', label: '단백질 (g)' },
                    { key: 'fat', label: '지방 (g)' },
                    { key: 'carbs', label: '탄수화물 (g)' },
                    { key: 'sodium', label: '나트륨 (mg)' },
                    { key: 'sugar', label: '당류 (g)' },
                  ].map((f) => (
                    <div key={f.key}>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginBottom: 4 }}>
                        {f.label}
                      </div>
                      <input
                        className="form-input"
                        type="number"
                        step="0.1"
                        value={editForm.nutrition[f.key] ?? ''}
                        onChange={(e) => updateNutrition(f.key, e.target.value === '' ? '' : Number(e.target.value))}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeEdit} disabled={isSaving}>취소</button>
              <button className="btn btn-primary" onClick={save} disabled={isSaving}>
                {isSaving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AllergenPage;
